import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { getApiBaseUrl } from "../../../config/api";
import { getClassificationColor } from "../../../utils/gradingUtils";
import { ResultsPageContainer, ResponsiveTableWrap } from "./resultsUi";

const StudentTranscript = ({ basePath = "/Admin", readOnly = false }) => {
    const { studentId, id } = useParams();
    const { currentUser } = useSelector((s) => s.user);
    const resolvedId = studentId || id || (readOnly ? currentUser?._id : null);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!resolvedId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const { data: transcript } = await axios.get(`${getApiBaseUrl()}/transcript/${resolvedId}`);
                if (!cancelled) {
                    if (transcript?.message && !transcript?.student) {
                        setError(transcript.message);
                        setData(null);
                    } else {
                        setData(transcript);
                        setError("");
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.message || "Could not load transcript");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [resolvedId]);

    const student = data?.student;
    const schoolName = currentUser?.schoolName || "University";
    const classStyle = getClassificationColor(data?.degreeClassification);

    return (
        <ResultsPageContainer maxWidth="lg">
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} className="no-print">
                <Button startIcon={<ArrowBackOutlinedIcon />} onClick={() => navigate(-1)}>
                    Back
                </Button>
                <Box sx={{ flex: 1 }} />
                {data ? (
                    <Button variant="contained" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>
                        Print
                    </Button>
                ) : null}
            </Stack>

            {error ? (
                <Alert severity="warning" sx={{ mb: 2 }} className="no-print">
                    {error}
                </Alert>
            ) : null}

            {loading ? <Skeleton variant="rounded" height={400} className="no-print" /> : null}

            {data ? (
                <Box
                    className="print-area"
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        bgcolor: "background.paper",
                        p: { xs: 2, sm: 3 },
                        "@media print": { border: "none", p: 0 },
                    }}
                >
                    <Typography className="print-title" variant="h5" fontWeight={800} align="center">
                        {schoolName}
                    </Typography>
                    <Typography className="print-subtitle" variant="subtitle1" align="center" color="text.secondary">
                        Official Academic Transcript
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
                        University programme record · {new Date().toLocaleDateString()}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2">
                                <strong>Name:</strong> {student?.name}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Registration No:</strong> {student?.studentRegNumber || student?.rollNum || "—"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Programme:</strong> {student?.programme || "—"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2">
                                <strong>Faculty:</strong> {student?.faculty || "—"}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Department:</strong> {student?.department || "—"}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
                                <Typography variant="body2">
                                    <strong>Cumulative GPA:</strong> {Number(data.cumulativeGPA || 0).toFixed(2)}
                                </Typography>
                                <Chip
                                    label={data.degreeClassification || "—"}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        bgcolor: classStyle.bg,
                                        color: classStyle.color,
                                        borderColor: classStyle.border,
                                    }}
                                    variant="outlined"
                                />
                            </Stack>
                        </Grid>
                    </Grid>

                    {(data.grades_by_semester || []).length === 0 ? (
                        <Alert severity="info">No university grades recorded yet.</Alert>
                    ) : (
                        (data.grades_by_semester || []).map((group) => (
                            <Box key={`${group.academicYear}-${group.semester}`} sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                    {group.academicYear} · {group.semester}
                                    <Chip
                                        label={`GPA ${Number(group.semesterGPA || 0).toFixed(2)}`}
                                        size="small"
                                        sx={{ ml: 1 }}
                                    />
                                </Typography>
                                <ResponsiveTableWrap>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Subject</TableCell>
                                                <TableCell align="right">CA</TableCell>
                                                <TableCell align="right">Exam</TableCell>
                                                <TableCell align="right">Total</TableCell>
                                                <TableCell align="center">Grade</TableCell>
                                                <TableCell align="right">Credits</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(group.grades || []).map((g) => (
                                                <TableRow key={g.id || g._id}>
                                                    <TableCell>{g.subject?.subName || g.subjectId}</TableCell>
                                                    <TableCell align="right">{g.caScore}</TableCell>
                                                    <TableCell align="right">{g.examScore}</TableCell>
                                                    <TableCell align="right">{g.totalScore}</TableCell>
                                                    <TableCell align="center">{g.gradeLetter}</TableCell>
                                                    <TableCell align="right">{g.creditHours}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </ResponsiveTableWrap>
                            </Box>
                        ))
                    )}

                    <Grid container spacing={4} sx={{ mt: 4 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                                Registrar
                            </Typography>
                            <Box sx={{ borderBottom: "1px solid #000", height: 36, mb: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">
                                Signature &amp; date
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                                Dean / Principal
                            </Typography>
                            <Box sx={{ borderBottom: "1px solid #000", height: 36, mb: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">
                                Signature &amp; date
                            </Typography>
                        </Grid>
                    </Grid>

                    <Box
                        sx={{
                            width: 110,
                            height: 110,
                            border: "2px dashed #666",
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            mx: "auto",
                            mt: 2,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                            Official stamp
                        </Typography>
                    </Box>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, textAlign: "center" }}>
                        This is an official transcript generated from the school management system.
                    </Typography>
                </Box>
            ) : null}

            {resolvedId && !readOnly ? (
                <Button sx={{ mt: 2 }} className="no-print" variant="outlined" onClick={() => navigate(`${basePath}/students/student/marks/${resolvedId}`)}>
                    Edit grades
                </Button>
            ) : null}
        </ResultsPageContainer>
    );
};

export default StudentTranscript;
